import { PrepaymentProvider } from "../../../../common/enums";

export type ReservationConfirmAction = "fillForm" | "choosePayment" | "reserve";

export interface IResolveReservationConfirmActionInput {
  /** The class has an in-app pre-reservation form. */
  needsFormSubmission: boolean;
  /** The user already filled and agreed to it during this booking. */
  hasFormSubmission: boolean;
  requirePrepayment: boolean;
  isMultisportAvailable: boolean;
  isWaitlistBooking: boolean;
}

/**
 * What pressing "confirm" on the last wizard step should do.
 *
 * 1. An in-app form is a hard gate and always comes first.
 * 2. Prepaid classes, and classes that accept Multisport, go through the payment-method
 *    dialog. For a non-prepaid class the dialog only captures Multisport vs "pay on site";
 *    nothing is charged.
 * 3. Waitlist bookings skip the dialog — there is no confirmed spot to pay for yet.
 */
export const resolveReservationConfirmAction = ({
  needsFormSubmission,
  hasFormSubmission,
  requirePrepayment,
  isMultisportAvailable,
  isWaitlistBooking,
}: IResolveReservationConfirmActionInput): ReservationConfirmAction => {
  if (needsFormSubmission && !hasFormSubmission) {
    return "fillForm";
  }

  if ((requirePrepayment || isMultisportAvailable) && !isWaitlistBooking) {
    return "choosePayment";
  }

  return "reserve";
};

export interface IMissingStripeCheckoutUrlInput {
  requirePrepayment: boolean | undefined;
  prepaymentProvider: number | undefined;
  subscriptionPlanId: number | null;
  payWithMultisport: boolean;
  isWaitlist: boolean | undefined;
  stripeCheckoutUrl: string | null | undefined;
}

/**
 * True only when the API was expected to return a Stripe checkout URL and did not: a
 * prepaid Stripe class paid by card, not on the waitlist. Every other case legitimately
 * comes back without a URL — plan-paid, Multisport, waitlist, Link provider, and a
 * class whose prepayment is switched off (whatever provider it still carries).
 */
export const isMissingStripeCheckoutUrl = ({
  requirePrepayment,
  prepaymentProvider,
  subscriptionPlanId,
  payWithMultisport,
  isWaitlist,
  stripeCheckoutUrl,
}: IMissingStripeCheckoutUrlInput): boolean =>
  requirePrepayment === true &&
  prepaymentProvider === PrepaymentProvider.Stripe &&
  subscriptionPlanId === null &&
  !payWithMultisport &&
  !isWaitlist &&
  !stripeCheckoutUrl;
